import { createReducer, on } from '@ngrx/store';
import {
  IAginConnection,
  IAginConnectionPage,
  IAginPlatformWithConnections,
  IAginScope,
} from '@cde-fe-app/app/shared/models/agin.model';
import { ServiceCallError, ServiceCallResponse } from '@cde-fe-app/app/shared/models/service-call.model';
import { StateError } from '@cde-fe-app/app/shared/state';
import { aginActions } from './agin.actions';

export interface State {
  scopes: IAginScope[] | null;
  scopesPending: boolean;
  scopesError: string | null;

  platformsWithConnections: IAginPlatformWithConnections[] | null;
  platformsWithConnectionsPending: boolean;
  platformsWithConnectionsError: string | null;

  connections: IAginConnectionPage | null;
  connectionsPending: boolean;
  connectionsError: string | null;

  currentConnection: IAginConnection | null;
  currentConnectionPending: boolean;
  currentConnectionError: string | null;

  connectionCreation: {
    pending: boolean;
    error: string | null;
  };

  connectionRequestId: string | null;
  pendingConnectionData: {
    platformId: string;
    scopesGranted: string[];
  } | null;

  oauthFlow: {
    authorizationRedirectUri: string | null;
    authorizationPending: boolean;
    authorizationError: string | null;
    tokenExchangePending: boolean;
    tokenExchangeError: string | null;
  };

  platformFilters: {
    scopeFilter: string | null;
    nameFilter: string | null;
  };

  connectionsWithAssets: Record<string, IAginConnection> | null;
  connectionsWithAssetsPending: Record<string, boolean>;
  connectionsWithAssetsError: Record<string, string | null>;
}

export const initialState: State = {
  scopes: null,
  scopesPending: false,
  scopesError: null,

  platformsWithConnections: null,
  platformsWithConnectionsPending: false,
  platformsWithConnectionsError: null,

  connections: null,
  connectionsPending: false,
  connectionsError: null,

  currentConnection: null,
  currentConnectionPending: false,
  currentConnectionError: null,

  connectionCreation: {
    pending: false,
    error: null,
  },

  connectionRequestId: null,

  oauthFlow: {
    authorizationRedirectUri: null,
    authorizationPending: false,
    authorizationError: null,
    tokenExchangePending: false,
    tokenExchangeError: null,
  },

  platformFilters: {
    scopeFilter: null,
    nameFilter: null,
  },

  connectionsWithAssets: null,
  connectionsWithAssetsPending: {},
  connectionsWithAssetsError: {},

  pendingConnectionData: null,
};

function getErrorMessage(error: StateError | null): string {
  if (!error) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error instanceof ServiceCallError) {
    return error.message;
  }
  if (error instanceof ServiceCallResponse) {
    return error.data?.message || '';
  }
  console.warn('Unknown error type in AgIN reducer:', error);
  return '';
}

export const reducer = createReducer(
  initialState,

  on(aginActions.loadAginScopes, state => ({
    ...state,
    scopesPending: true,
    scopesError: null,
  })),

  on(aginActions.loadAginScopesSuccess, (state, { scopes }) => ({
    ...state,
    scopes,
    scopesPending: false,
  })),

  on(aginActions.loadAginScopesFailure, (state, { error }) => ({
    ...state,
    scopesPending: false,
    scopesError: getErrorMessage(error),
  })),

  on(aginActions.loadAginPlatformsWithConnections, state => ({
    ...state,
    platformsWithConnectionsPending: true,
    platformsWithConnectionsError: null,
  })),

  on(aginActions.loadAginPlatformsWithConnectionsSuccess, (state, { platforms }) => ({
    ...state,
    platformsWithConnections: platforms,
    platformsWithConnectionsPending: false,
  })),

  on(aginActions.loadAginPlatformsWithConnectionsFailure, (state, { error }) => ({
    ...state,
    platformsWithConnectionsPending: false,
    platformsWithConnectionsError: getErrorMessage(error),
  })),

  on(aginActions.createAginConnection, (state, { connectionRequest }) => ({
    ...state,
    connectionCreation: {
      pending: true,
      error: null,
    },
    pendingConnectionData: {
      platformId: connectionRequest.platformResponding.platformId,
      scopesGranted: connectionRequest.platformRequesting.scopesGranted,
    },
  })),

  on(aginActions.createAginConnectionSuccess, (state, { connectionRequestId }) => {
    const newState = {
      ...state,
      connectionCreation: {
        pending: false,
        error: null,
      },
      connectionRequestId,
    };
    return newState;
  }),

  on(aginActions.createAginConnectionFailure, (state, { error }) => ({
    ...state,
    connectionCreation: {
      pending: false,
      error: getErrorMessage(error),
    },
  })),

  on(aginActions.loadAginConnections, state => ({
    ...state,
    connectionsPending: true,
    connectionsError: null,
  })),

  on(aginActions.loadAginConnectionsSuccess, (state, { connections }) => ({
    ...state,
    connections,
    connectionsPending: false,
  })),

  on(aginActions.loadAginConnectionsFailure, (state, { error }) => ({
    ...state,
    connectionsPending: false,
    connectionsError: getErrorMessage(error),
  })),

  on(aginActions.loadAginConnection, state => ({
    ...state,
    currentConnectionPending: true,
    currentConnectionError: null,
  })),

  on(aginActions.loadAginConnectionSuccess, (state, { connection }) => ({
    ...state,
    currentConnection: connection,
    currentConnectionPending: false,
  })),

  on(aginActions.loadAginConnectionFailure, (state, { error }) => ({
    ...state,
    currentConnectionPending: false,
    currentConnectionError: getErrorMessage(error),
  })),

  on(aginActions.deleteAginConnection, state => ({
    ...state,
    currentConnectionPending: true,
    currentConnectionError: null,
  })),

  on(aginActions.deleteAginConnectionSuccess, (state, { connectionId }) => ({
    ...state,
    currentConnection: null,
    currentConnectionPending: false,
    connections: state.connections
      ? {
          ...state.connections,
          content: state.connections.content.filter(conn => conn.connectionId !== connectionId),
          totalElements: state.connections.totalElements - 1,
          numberOfElements: state.connections.numberOfElements - 1,
        }
      : null,
  })),

  on(aginActions.deleteAginConnectionFailure, (state, { error }) => ({
    ...state,
    currentConnectionPending: false,
    currentConnectionError: getErrorMessage(error),
  })),

  on(aginActions.exchangeAginCodeForToken, state => ({
    ...state,
    oauthFlow: {
      ...state.oauthFlow,
      tokenExchangePending: true,
      tokenExchangeError: null,
    },
  })),

  on(aginActions.exchangeAginCodeForTokenSuccess, state => ({
    ...state,
    oauthFlow: {
      ...state.oauthFlow,
      tokenExchangePending: false,
    },
  })),

  on(aginActions.exchangeAginCodeForTokenFailure, (state, { error }) => ({
    ...state,
    oauthFlow: {
      ...state.oauthFlow,
      tokenExchangePending: false,
      tokenExchangeError: getErrorMessage(error),
    },
  })),

  on(aginActions.setPlatformFilters, (state, { filters }) => ({
    ...state,
    platformFilters: filters,
  })),

  on(aginActions.clearPlatformFilters, state => ({
    ...state,
    platformFilters: {
      scopeFilter: null,
      nameFilter: null,
    },
  })),

  on(aginActions.getAginAuthorization, state => ({
    ...state,
    oauthFlow: {
      ...state.oauthFlow,
      authorizationPending: true,
      authorizationError: null,
    },
  })),

  on(aginActions.getAginAuthorizationSuccess, (state, { response }) => ({
    ...state,
    connectionRequestId: null,
    oauthFlow: {
      ...state.oauthFlow,
      authorizationRedirectUri: response.redirectUri,
      authorizationPending: false,
    },
  })),

  on(aginActions.getAginAuthorizationFailure, (state, { error }) => ({
    ...state,
    oauthFlow: {
      ...state.oauthFlow,
      authorizationPending: false,
      authorizationError: getErrorMessage(error),
    },
  })),

  on(aginActions.loadAginConnectionWithAssets, (state, { connectionId }) => ({
    ...state,
    connectionsWithAssetsPending: {
      ...state.connectionsWithAssetsPending,
      [connectionId]: true,
    },
    connectionsWithAssetsError: {
      ...state.connectionsWithAssetsError,
      [connectionId]: null,
    },
  })),

  on(aginActions.loadAginConnectionWithAssetsSuccess, (state, { connection }) => ({
    ...state,
    connectionsWithAssets: {
      ...state.connectionsWithAssets,
      [connection.connectionId]: connection,
    },
    connectionsWithAssetsPending: {
      ...state.connectionsWithAssetsPending,
      [connection.connectionId]: false,
    },
  })),

  on(aginActions.loadAginConnectionWithAssetsFailure, (state, { error, connectionId }) => ({
    ...state,
    connectionsWithAssetsPending: {
      ...state.connectionsWithAssetsPending,
      [connectionId]: false,
    },
    connectionsWithAssetsError: {
      ...state.connectionsWithAssetsError,
      [connectionId]: getErrorMessage(error),
    },
  })),

  on(aginActions.clearAginConnectionsWithAssets, state => ({
    ...state,
    connectionsWithAssets: null,
    connectionsWithAssetsPending: {},
    connectionsWithAssetsError: {},
  })),

  on(aginActions.clearPendingConnectionData, state => ({
    ...state,
    pendingConnectionData: null,
  }))
);
